// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

export const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, isFormData, method, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = !isFormData
    ? {
      credentials: credentials || 'include',
      // Since server/api mostly talks to Marketplace API using SDK,
      // we default to 'application/transit+json' as content type (as SDK uses transit).
      headers: headers || { 'Content-Type': 'application/transit+json' },
      method,
      ...bodyMaybe,
      ...rest,
    }
    : { method, body };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        const e = new Error(data?.error || 'Request failed');
        Object.assign(e, data);
        e.status = res.status;
        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}, isFormData = false) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
    isFormData,
  };

  return request(path, requestOptions, isFormData);
};

const get = (path, options) => {
  const reqOptions = {
    ...options,
    method: methods.GET,
  };

  return request(path, reqOptions);
};

const del = (path, options) => {
  const reqOptions = {
    ...options,
    method: methods.DELETE,
  };

  return request(path, reqOptions);
};

const put = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.PUT,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const handleCustomerCancelRefund = body => {
  return post('/api/customer-cancel-refund', body);
};

export const setPaymentIntentIdToTransaction = body => {
  return post('/api/transactions/set-payment-intent-id', body);
};

export const setIsInstantBookingToTransaction = body => {
  return post('/api/transactions/set-is-instant-booking', body);
};

export const setGoogleCalendarUrlToTransaction = body => {
  return post('/api/transactions/set-google-calendar-url', body);
};

export const generateIcsFile = body => {
  return post('/api/transactions/generate-ics-file', body);
};

export const getListingTypesIds = () => {
  return get('/api/transactions/listing-types-ids');
};

export const sendRequestToCancelByProvider = body => {
  return post('/api/transactions/request-cancel-by-provider', body);
};

export const sendUserEmailForSubscription = body => {
  return post('/api/subscriptions/send-user-email', body);
};

export const getCountListingsByFilters = body => {
  return post('/api/listings/count-listings-by-filters', body);
};

export const searchPlacesForListing = body => {
  return post('/api/listings/search-places-for-listing', body);
};

export const selectPlaceForListing = body => {
  return post('/api/listings/select-place-for-listing', body);
};

export const getPlaceReviewsForListing = body => {
  return post('/api/listings/get-place-reviews-for-listing', body);
};

export const updatePlaceDetailsForListing = body => {
  return put('/api/listings/update-place-details-for-listings', body);
};

export const setRating = body => {
  return post('/api/listing/set-rating', body);
}

export const saveListingTranslation = body => {
  return post('/api/listings/save-listing-translation', body);
};

export const getListingTranslation = ({ listingId }) => {
  return get(`/api/listings/get-listing-translation/${listingId}`);
};

export const getExternalServices = ({ listingId }) => {
  return get(`/api/listings/external-services/${listingId}`);
};

export const changeExternalServices = body => {
  return post('/api/listings/external-services', body);
}

export const getExternalServicesEvents = ({ listingId }) => {
  return get(`/api/listings/external-services-events/${listingId}`);
};

export const changeListingAvailabilityExceptions = body => {
  return post('/api/listing/availability-exceptions', body);
};

export const updateListingSeasonalAvailabilityAfterDeleteException = body => {
  return post('/api/listing/availability-exceptions/update', body);
};

export const getRewildingInfoFiles = () => {
  return get('/api/rewilding-info-files');
};

export const getRewildingInfoFilesFromListing = ({ listingId }) => {
  return get(`/api/rewilding-info-files-from-listing/${listingId}`);
};

export const uploadRewildingInfoFiles = body => {
  return post('/api/rewilding-info-files', body, {}, true);
};

export const deleteRewildingInfoFiles = body => {
  return post('/api/rewilding-info-files/bulk-delete', body);
};

export const createRewildingInfoLinks = body => {
  return post('/api/rewilding-info-files/create-links', body);
};

export const setIsShowRewildingInfoFilesToTransaction = body => {
  return post('/api/rewilding-info-files/set-is-show-rewilding-info-files', body);
};

export const azureTranslate = body => {
  return post('/api/azure/translate', body);
};

export const getCMSPage = pageId => {
  return get(`/api/cms-pages/${pageId}`);
};

export const updateCMSPage = body => {
  return post('/api/cms-pages', body);
};

export const saveUserTranslation = body => {
  return post('/api/users/save-user-translation', body);
};

export const getUserTranslation = ({ userId }) => {
  return get(`/api/users/get-user-translation/${userId}`);
};
